import { Dispatch, SetStateAction } from "react"
import { Col, Form, OverlayTrigger, Popover, Row, Table } from "react-bootstrap"
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
  TableRow,
} from "react-bs-datatable"
import { getPortStaysActivity } from "../../helpers/getPortStaysActivity"
import { usePrevetTabStateContext } from "../../hooks/prevetContext"
import { DDCategories, VesselStay } from "../../hooks/useFetchVesselStays"
import { TABLE_HEADERS } from "../PrevetVesselHistoryTableHeaders"
import classes from "./vessel-history-table.module.scss"

interface PrevetVesselHistoryProps {
  vesselStays: VesselStay[]
  setVesselState: Dispatch<
    SetStateAction<{
      includeBallast: boolean
      includeLaden: boolean
    }>
  >
  vesselState: {
    includeBallast: boolean
    includeLaden: boolean
  }
  setFilterText: Dispatch<SetStateAction<string>>
  filterText: string
  setCurrentPage: Dispatch<SetStateAction<number>>
  currentPage: number
  setRowsPerPage: Dispatch<SetStateAction<number>>
  rowsPerPage: number
}

const renderTooltip = () => {
  return (
    <Popover id="popover-basic">
      <Popover.Body>Prolonged Port Stay</Popover.Body>
    </Popover>
  )
}

export function PrevetVesselHistory({
  vesselStays,
  setVesselState,
  vesselState,
  setFilterText,
  filterText,
  setCurrentPage,
  currentPage,
  setRowsPerPage,
  rowsPerPage,
}: PrevetVesselHistoryProps) {
  const headerProps = TABLE_HEADERS.map((header) => header.prop)

  const { setTabStateValue } = usePrevetTabStateContext()

  const handleVesselStateChange = (name: "includeBallast" | "includeLaden") => {
    setVesselState((prevState) => {
      if (name === "includeBallast") {
        setTabStateValue("hvIncludeBallast", !prevState[name], undefined)
      }
      if (name === "includeLaden") {
        setTabStateValue("hvIncludeLaden", !prevState[name], undefined)
      }
      const newState = { ...prevState, [name]: !prevState[name] }

      // Ensure at least one checkbox remains checked
      if (!newState.includeBallast && !newState.includeLaden) {
        return prevState // Prevent update
      }

      return newState
    })
  }

  const filteredData = vesselStays.filter((data) => {
    if (!vesselState.includeBallast && data.leg === "ballast") return false // Filter out ballast rows
    if (!vesselState.includeLaden && data.leg === "laden") return false // Filter out laden rows
    return true
  })

  const arrayForPagination = [...filteredData].filter((item) => {
    return (
      headerProps.some((key) => {
        const value = item[key as keyof VesselStay]
        return (
          value !== null &&
          value !== undefined &&
          value.toString().toLowerCase().includes(filterText.toLowerCase())
        )
      }) ||
      (getPortStaysActivity(item)
        ?.toLowerCase()
        .includes(filterText.toLowerCase()) ??
        false)
    )
  })

  const totalPages = Math.ceil(arrayForPagination.length / rowsPerPage)
  const clampedPage = Math.max(1, Math.min(currentPage, totalPages))
  const startIndex = (clampedPage - 1) * rowsPerPage
  const endIndex = startIndex + rowsPerPage

  const finalFilteredData = arrayForPagination.slice(startIndex, endIndex)

  const handleFilterChange = (e: string) => {
    setTabStateValue("hvSearchText", e, undefined)
    setFilterText(e)
    setTabStateValue("hvCurrentPage", 1, undefined)
    setCurrentPage(1)
  }

  const handlePageChange = (e: number) => {
    setTabStateValue("hvCurrentPage", e, undefined)
    setCurrentPage(e)
  }

  const handlePageSizeChange = (e: any) => {
    setTabStateValue("hvRowsPerPage", e, undefined)
    setRowsPerPage(Number(e))
  }

  return (
    <DatatableWrapper
      body={finalFilteredData}
      headers={TABLE_HEADERS}
      sortProps={{
        initialState: {
          prop: "date",
          order: "desc" as const,
        },
      }}
      paginationOptionsProps={{
        initialState: {
          rowsPerPage: 10,
          options: [5, 10, 50, 100],
        },
      }}
    >
      <Row className={`${classes.Header} mb-4 p-2 align-items-center`}>
        <Col
          xs={12}
          className="d-flex justify-content-between align-items-center"
        >
          {/* Title */}
          <h4 className="flex-1 card-title mt-2 mb-1">Historical Voyages</h4>
          <div className="d-flex">
            {/* Checkboxes */}
            <Form className={`${classes.Form}`}>
              <div className="d-flex align-items-start">
                <Form.Check
                  type="checkbox"
                  checked={vesselState.includeBallast}
                  onChange={() => handleVesselStateChange("includeBallast")}
                  className="mb-0"
                />
                <Form.Label className="w-100 me-4 mb-0">Ballast</Form.Label>
                <Form.Check
                  type="checkbox"
                  checked={vesselState.includeLaden}
                  onChange={() => handleVesselStateChange("includeLaden")}
                  className="mb-0"
                />
                <Form.Label className="w-100 mb-0">Laden</Form.Label>
              </div>
            </Form>

            {/* Filter */}
            <div className="ms-3">
              <Filter
                classes={{
                  inputGroup: "PrevetVesselHistory_FilterInput",
                  clearButton: "PrevetVesselHistory_ClearButton",
                }}
                placeholder="Search voyage"
                controlledProps={{
                  filter: filterText,
                  onFilterChange: handleFilterChange,
                }}
              />
            </div>
          </div>
        </Col>
      </Row>
      <Table>
        <TableHeader />
        <TableBody
          rowProps={(row: VesselStay) => {
            if (row.yard_activity && DDCategories.includes(row.yard_activity)) {
              return {
                // className: "PrevetVesselHistory_Row--success"
                className: `${classes[`Row--success`]}`,
              }
            }
            if (row.duration >= 20) {
              return {
                className: `${classes[`Row--danger`]}`,
              }
            }
            if (row.duration >= 10) {
              return {
                className: `${classes[`Row--warning`]}`,
              }
            }
            return {}
          }}
          children={(rows) => {
            return rows.map((data, idx) => {
              const rowColorClass = getRowColor(data)
              if (data.duration >= 10) {
                return (
                  <OverlayTrigger
                    key={`${data.date_from}_${data.date_to}`}
                    overlay={renderTooltip()}
                    placement="top"
                  >
                    {({ ref, ...triggerHandler }) => (
                      <TableRow
                        key={`${data.date_from}_${data.date_to}`}
                        classes={{ tr: `${rowColorClass}` }}
                        rowData={data}
                        rowIdx={idx}
                        rowProps={{ ref: ref, ...triggerHandler }}
                      />
                    )}
                  </OverlayTrigger>
                )
              }
              return (
                <TableRow
                  key={`${data.date_from}_${data.date_to}`}
                  classes={{ tr: `${rowColorClass}` }}
                  rowData={data}
                  rowIdx={idx}
                />
              )
            })
          }}
        />
      </Table>
      <Row
        className={`${classes.Pagination} d-flex flex-row justify-content-between`}
      >
        <PaginationOptions
          labels={{ beforeSelect: "Show results" }}
          controlledProps={{
            rowsPerPage: rowsPerPage,
            onRowsPerPageChange: handlePageSizeChange,
          }}
        />
        <Pagination
          controlledProps={{
            currentPage: currentPage,
            maxPage: totalPages,
            onPaginationChange: handlePageChange,
          }}
        />
      </Row>
    </DatatableWrapper>
  )
}

function getRowColor(row: VesselStay) {
  if (row.yard_activity && DDCategories.includes(row.yard_activity)) {
    return "PrevetVesselHistory_Row--success"
  }
  if (row.duration >= 20) {
    return "PrevetVesselHistory_Row--danger"
  }
  if (row.duration >= 10) {
    return "PrevetVesselHistory_Row--warning"
  }
}
